import { createAuthenticatedClient } from "../utils/apiClient";
import { getStoredToken } from "../utils/installAuthToken";

const BASE_URL = "https://codex.mrc.ai/api";

export async function getCodeList(tableId) {
  const response = await fetch(`${BASE_URL}/codelists/${tableId}`);
  if (!response.ok) throw new Error("Failed to fetch CodeList");
  return await response.json();
}

export async function getCode(version, codelist, longCode) {
  const url = `${BASE_URL}/${version}/codelists/${codelist}/codes/${longCode}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error("Failed to fetch Code");
  return await response.json();
}

// --- Code list editing (the Codex screen) ---
// Through the authenticated client: it adds the token, unwraps the
// envelope and rejects with error.code / error.message.
const codexClient = () => {
  const token = getStoredToken();
  if (!token) throw new Error("No authentication token available");
  return createAuthenticatedClient(token);
};
const codexGet = async (path, config) => (await codexClient().get(`${BASE_URL}${path}`, config)).data;
const asList = (data) => (Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);

/** Every code list's header: { _id, tableID, name, dataStandard, dataStandardVersion, agency }. */
export const listCodeLists = async () => asList(await codexGet("/codelists"));

/** One code list with its codes, by its ID (tableID). */
export const loadCodeList = (tableId) => codexGet(`/codelists/${encodeURIComponent(tableId)}`);

export const createCodeList = async (codeList) => (await codexClient().post(`${BASE_URL}/codelists`, codeList)).data;

/** Replaces a code list, by its _id. */
export const updateCodeList = async (id, codeList) =>
  (await codexClient().put(`${BASE_URL}/codelists/${encodeURIComponent(id)}`, codeList)).data;

/** Copies a code list and its codes to a new ID. */
export const copyCodeList = async (tableId, newTableId) =>
  (await codexClient().post(`${BASE_URL}/codelists/${encodeURIComponent(tableId)}/copy`, { newCodeList: newTableId })).data;

/** The data standards: [{ longCode, name, description }]. */
export const listStandards = async () => asList(await codexGet("/standards"));

/** A data standard's versions: [{ version, name, ... }]. */
export const listStandardVersions = async (standardCode) =>
  asList(await codexGet(`/standards/${encodeURIComponent(standardCode)}/versions`));

/** A code list as CSV text. */
export const exportCodeListCsv = (tableId) =>
  codexGet(`/codelists/${encodeURIComponent(tableId)}/csv`, { responseType: "text", transformResponse: (d) => d });
